
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

export interface Notification {
  id: string;
  user_id: string;
  actor_id: string;
  type: 'like' | 'follow' | 'comment' | 'mention' | 'relic' | 'save' | 'reloop' | 'verified';
  read_at: string | null;
  created_at: string;
  bulletin_id?: string;
  loop_id?: string;
  image_url?: string;
  actor_username?: string;
  actor_profile_image?: string;
  is_deleted?: boolean;
  profiles?: {
    username: string;
    profile_image_url: string;
  };
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  showNotifications: boolean;
  setShowNotifications: (show: boolean) => void;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchNotifications = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          id, 
          user_id, 
          actor_id, 
          type, 
          read_at, 
          created_at, 
          bulletin_id,
          loop_id,
          image_url,
          profiles!actor_id(
            username,
            profile_image_url
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const notificationsWithExistenceCheck = await Promise.all(
        data.map(async (notification: any) => {
          let isDeleted = false;
          
          if (notification.bulletin_id && 
             (notification.type === 'like' || notification.type === 'comment' || notification.type === 'mention' || notification.type === 'save' || notification.type === 'reloop')) {
            const { data: bulletinData } = await supabase
              .from('bulletins')
              .select('id')
              .eq('id', notification.bulletin_id)
              .maybeSingle();
              
            if (!bulletinData) {
              isDeleted = true;
            }
          }
          
          // Check if loop still exists for loop-related notifications
          if (notification.loop_id && 
             (notification.type === 'like' || notification.type === 'comment' || notification.type === 'mention' || notification.type === 'save' || notification.type === 'reloop')) {
            const { data: loopData } = await supabase
              .from('loops')
              .select('id')
              .eq('id', notification.loop_id)
              .maybeSingle();
              
            if (!loopData) {
              isDeleted = true;
            }
          }

          let profileData;
          if (notification.profiles) {
            if (Array.isArray(notification.profiles)) {
              profileData = notification.profiles[0] || { username: 'Unknown user', profile_image_url: '' };
            } else {
              profileData = notification.profiles;
            }
          } else {
            profileData = { username: 'Unknown user', profile_image_url: '' };
          }

          return {
            ...notification,
            actor_username: profileData.username || 'Unknown user',
            actor_profile_image: profileData.profile_image_url || '',
            is_deleted: isDeleted,
            profiles: {
              username: profileData.username || 'Unknown user',
              profile_image_url: profileData.profile_image_url || ''
            }
          } as Notification;
        })
      );

      setNotifications(notificationsWithExistenceCheck);
      const unread = notificationsWithExistenceCheck.filter(n => !n.read_at).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    console.log('Setting up realtime notifications subscription');
    
    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          console.log('New notification received:', payload);
          
          const { data, error } = await supabase
            .from('notifications')
            .select(`
              id, 
              user_id, 
              actor_id, 
              type, 
              read_at, 
              created_at, 
              bulletin_id,
              loop_id,
              profiles!actor_id(
                username,
                profile_image_url
              )
            `)
            .eq('id', payload.new.id)
            .single();
            
          if (error) {
            console.error('Error fetching new notification details:', error);
            return;
          }

          // Fix TypeScript error: Properly handle profiles data
          let profileData = { username: 'Unknown user', profile_image_url: '' };
          if (data.profiles) {
            // Handle both array and object formats
            if (Array.isArray(data.profiles)) {
              profileData = data.profiles[0] || { username: 'Unknown user', profile_image_url: '' };
            } else {
              profileData = data.profiles;
            }
          }

          const processedNotification = {
            ...data,
            actor_username: profileData.username || 'Unknown user',
            actor_profile_image: profileData.profile_image_url || '',
            profiles: {
              username: profileData.username || 'Unknown user',
              profile_image_url: profileData.profile_image_url || ''
            }
          } as Notification;

          setNotifications(prev => [processedNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          toast({
            title: "New Notification",
            description: getNotificationMessage(processedNotification),
          });
        }
      )
      .subscribe((status) => {
        console.log('Notifications subscription status:', status);
      });

    return () => {
      console.log('Cleaning up notifications subscription');
      supabase.removeChannel(channel);
    };
  }, [user?.id, toast]);

  const getNotificationMessage = (notification: Notification): string => {
    const deletedSuffix = notification.is_deleted ? " (content no longer available)" : "";
    
    switch (notification.type) {
      case 'like':
        if (notification.loop_id) {
          return `@${notification.actor_username} liked your loop${deletedSuffix}`;
        }
        return `@${notification.actor_username} liked your bulletin${deletedSuffix}`;
      case 'follow':
        return `@${notification.actor_username} followed you`;
      case 'comment':
        if (notification.loop_id) {
          return `@${notification.actor_username} commented on your loop${deletedSuffix}`;
        }
        return `@${notification.actor_username} commented on your bulletin${deletedSuffix}`;
      case 'mention':
        if (notification.loop_id) {
          return `@${notification.actor_username} mentioned you in a loop${deletedSuffix}`;
        }
        return `@${notification.actor_username} mentioned you${deletedSuffix}`;
      case 'relic':
        return 'Your bulletin has become a relic! 💎';
      case 'save':
        return `@${notification.actor_username} saved your post to a moodboard${deletedSuffix}`;
      case 'reloop':
        if (notification.loop_id) {
          return `@${notification.actor_username} relooped your loop${deletedSuffix}`;
        }
        return `@${notification.actor_username} relooped your post${deletedSuffix}`;
      default:
        return 'You have a new notification';
    }
  };

  const markAsRead = async (id: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id
            ? { ...notification, read_at: new Date().toISOString() }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id || unreadCount === 0) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          read_at: notification.read_at || new Date().toISOString(),
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        showNotifications,
        setShowNotifications,
        markAsRead,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
